<template>
  <main id="magazzino" class="page-container stock-list">
    <div class="table-container-stock">
      <div class="magazzino-filters">
        <!-- [sc-48337] Campo avvolto per poter posizionare la "x": la barra
             filtri è un flex di controlli, e senza contenitore il pulsante non
             avrebbe un riferimento su cui ancorarsi. -->
        <div class="filter-search-wrap">
          <input
            v-model="filterSearch"
            @keyup.enter="onSearchNow"
            class="filter-input filter-input--search"
            placeholder="Cerca per codice, nome, brand o formato…"
            type="text"
          />
          <button
            v-if="filterSearch"
            @click="onResetSearch"
            class="clear-search-button"
            :disabled="isLoading"
            title="Azzera la ricerca"
            type="button"
          >
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <select
          v-model="filterCategory"
          class="filter-select"
          :disabled="isLoading"
        >
          <option value="">Tutte le categorie</option>
          <option v-for="cat in CATEGORIES" :key="cat" :value="cat">
            {{ cat }}
          </option>
        </select>
        <label class="filter-checkbox">
          <input
            type="checkbox"
            v-model="filterInStock"
            :disabled="isLoading"
          />
          Disponibili in magazzino
        </label>
        <!-- [sc-48280] Comprimi/estendi: in compresso restano Codice, Nome,
             Giacenza e Azioni, e la tabella entra nello schermo senza scroll
             orizzontale. Compare solo sotto i 1024px, dove il problema esiste:
             su desktop la tabella ci sta e il comando sarebbe rumore. -->
        <button
          v-if="isSmallScreen"
          class="refresh-btn compact-toggle"
          type="button"
          @click="toggleCompact"
          :title="
            compact
              ? 'Mostra tutte le colonne della tabella'
              : 'Mostra solo codice, nome e giacenza'
          "
        >
          <i
            :class="['fa-solid', compact ? 'fa-table-columns' : 'fa-compress']"
          ></i>
          <!-- [sc-48280] L'etichetta dice cosa succede premendo, non come si
               chiama lo stato: "Comprimi"/"Estendi" lasciava indovinare. -->
          <span>{{ compact ? "Mostra altre colonne" : "Mostra meno" }}</span>
        </button>
        <button
          class="refresh-btn"
          :class="{ refreshed }"
          @click="handleRefresh"
          :disabled="refreshing || isLoading"
          title="Aggiorna"
          style="margin-left: auto"
        >
          <i
            :class="[
              'fa-solid',
              refreshed ? 'fa-check' : 'fa-arrows-rotate',
              { 'fa-spin': refreshing },
            ]"
          ></i>
          <span>{{
            refreshing ? "Aggiorna…" : refreshed ? "Aggiornato!" : "Aggiorna"
          }}</span>
        </button>
      </div>

      <div class="stock-table-region">
        <div ref="tblWrap" class="tbl-wrap" :class="{ dimmed: isLoading }">
          <table class="tbl item-table">
            <thead>
              <tr>
                <th>Codice</th>
                <th>Nome</th>
                <!-- [sc-48247] Senza questa colonna due varianti dello stesso
                     codice sono righe identiche con giacenze diverse. -->
                <th v-if="showVariantCol">Variante</th>
                <th v-if="showBrandCol">Brand</th>
                <th v-if="showCategoryCol">Categoria</th>
                <th class="col-giacenza">Giacenza</th>
                <th v-if="showBoxCol">{{ boxColLabel }}</th>
                <th v-if="showSqmCol">M² tot.</th>
                <th v-if="showMlCol">ML tot.</th>
                <!-- [sc-48280] La colonna Azioni è sticky: collassata occupa
                     una striscia, estesa le tre icone. Nello screenshot della
                     review, estesa su 535px copriva la colonna Giacenza — il
                     numero per cui questa pagina esiste. -->
                <th
                  class="actions-head"
                  :class="{
                    'actions-head--collapsed': actionsCollapsed,
                    'actions-head--overlapping': actionsOverlapping,
                  }"
                >
                  <button
                    class="actions-toggle"
                    type="button"
                    @click="actionsCollapsed = !actionsCollapsed"
                    :title="
                      actionsCollapsed
                        ? 'Mostra le azioni'
                        : 'Nascondi le azioni'
                    "
                    :aria-expanded="String(!actionsCollapsed)"
                  >
                    <i
                      class="fa-solid fa-angle-right"
                      :style="
                        actionsCollapsed ? 'transform: rotate(180deg)' : ''
                      "
                    ></i>
                  </button>
                  <span v-if="!actionsCollapsed">Azioni</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <!-- [sc-48280] La riga apre l'aggiornamento giacenze. Su mobile
                   la matita è in fondo a una tabella più larga dello schermo:
                   il bersaglio diventa la riga intera, che è anche il gesto
                   che ci si aspetta da una lista su touch. -->
              <tr
                v-for="item in filteredItems"
                :key="item.id"
                class="item-row item-row--clickable"
                :class="{ 'item-row--critical': criticalItemIds[item.id] }"
                @click="openEdit(item)"
              >
                <td class="td-name" :title="item.supplierCode || item.itemCode">
                  {{ item.supplierCode || item.itemCode }}
                </td>
                <td class="td-name" :title="item.itemName">
                  {{ item.itemName }}
                </td>
                <td
                  v-if="showVariantCol"
                  class="td-name"
                  :title="variantLabel(item) ?? ''"
                >
                  {{ variantLabel(item) ?? "—" }}
                </td>
                <td v-if="showBrandCol">{{ item.brand ?? "—" }}</td>
                <td v-if="showCategoryCol">{{ item.category }}</td>
                <td class="col-giacenza">
                  {{ item.warehouse?.inWarehouse ?? 0 }}
                  <span
                    v-if="
                      !['area', 'linear'].includes(itemGroup(item)) &&
                      item.totalDamagedQty > 0
                    "
                    class="damaged-badge"
                    :title="`${item.totalDamagedQty} pz danneggiati su carichi merce ricevuti`"
                  >
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    {{ item.totalDamagedQty }}
                  </span>
                </td>
                <td v-if="showBoxCol">
                  {{
                    ["area", "linear"].includes(itemGroup(item))
                      ? item.warehouse?.boxQuantity ?? "—"
                      : "—"
                  }}
                  <span
                    v-if="
                      ['area', 'linear'].includes(itemGroup(item)) &&
                      item.totalDamagedQty > 0
                    "
                    class="damaged-badge"
                    :title="`${item.totalDamagedQty} sc. danneggiate su carichi merce ricevuti`"
                  >
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    {{ item.totalDamagedQty }}
                  </span>
                </td>
                <td v-if="showSqmCol">
                  {{
                    itemGroup(item) === "area" &&
                    Number(item.warehouse?.sqmTotal) > 0
                      ? formatDecimal(item.warehouse.sqmTotal)
                      : "—"
                  }}
                </td>
                <td v-if="showMlCol">
                  {{
                    itemGroup(item) === "linear" &&
                    Number(item.warehouse?.mlTotal) > 0
                      ? formatDecimal(item.warehouse.mlTotal)
                      : "—"
                  }}
                </td>
                <!-- [sc-48280] `.stop` su ognuna: la riga è cliccabile, e
                     senza questo premere l'orologio aprirebbe anche
                     l'aggiornamento giacenze.
                     Collassata la cella non si svuota: i bottoni restano nel
                     DOM e diventano invisibili, ritagliati dalla larghezza
                     della cella. Solo così l'altezza della riga è identica nei
                     due stati — è lo stesso contenuto — invece di doverla
                     ricostruire con misure che vanno tenute allineate a mano.
                     `tabindex="-1"` li toglie dal giro del TAB mentre sono
                     nascosti. Il chevron per riaprire sta solo
                     nell'intestazione: ripetuto su ogni riga si sovrapponeva
                     all'azzurro della colonna Giacenza. -->
                <td
                  class="actions-cell"
                  :class="{
                    'actions-cell--collapsed': actionsCollapsed,
                    'actions-cell--overlapping': actionsOverlapping,
                  }"
                >
                  <button
                    class="icons-button"
                    @click.stop="openEdit(item)"
                    title="Aggiorna giacenze"
                    type="button"
                    :tabindex="actionsCollapsed ? -1 : 0"
                  >
                    <i class="fa-regular fa-pen-to-square"></i>
                  </button>
                  <button
                    class="icons-button"
                    @click.stop="openHistory(item)"
                    title="Storico movimenti"
                    type="button"
                    :tabindex="actionsCollapsed ? -1 : 0"
                  >
                    <i class="fa-regular fa-clock"></i>
                  </button>
                  <button
                    class="icons-button"
                    :class="{
                      'btn-critical--active': criticalItemIds[item.id],
                    }"
                    @click.stop="toggleCritical(item.id)"
                    title="Segnala criticità"
                    type="button"
                    :tabindex="actionsCollapsed ? -1 : 0"
                  >
                    <i class="fa-solid fa-triangle-exclamation"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="filteredItems.length === 0">
                <td
                  :colspan="colCount"
                  style="text-align: center; padding: 16px; color: #888"
                >
                  Nessun articolo trovato.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- [sc-48280] Barra di scorrimento disegnata da noi.
             La scrollbar nativa la nasconde il sistema operativo quando non si
             sta scorrendo — su mobile e su macOS è il default e nessuna
             proprietà CSS lo impedisce in modo affidabile: `overflow-x: scroll`
             e la stilizzazione `::-webkit-scrollbar` non erano bastati. Questa
             è un elemento normale, quindi resta a schermo finché c'è qualcosa
             da scorrere, ed è anche trascinabile. -->
        <div
          v-if="scrollable"
          ref="scrollbar"
          class="table-scrollbar"
          @pointerdown="onScrollbarPointerDown"
          @pointermove="onScrollbarPointerMove"
          @pointerup="onScrollbarPointerUp"
          @pointercancel="onScrollbarPointerUp"
        >
          <div
            class="table-scrollbar__thumb"
            :style="{ width: thumbWidth + '%', left: thumbLeft + '%' }"
          ></div>
        </div>

        <!-- LOADER OVERLAY (limitato alla sola regione tabella) -->
        <div
          v-if="isLoading"
          class="loader-overlay"
          aria-live="polite"
          aria-busy="true"
        >
          <div class="spinner" role="status"></div>
          <p>Carico il magazzino…</p>
        </div>
      </div>

      <div class="pagination">
        <div class="pagination-left"></div>
        <!-- [sc-48337] Avanti/indietro al posto dei numeri di pagina: a
             cursore il database non sa quante pagine ci sono, ed è proprio non
             doverlo sapere che rende il costo costante al crescere del
             catalogo. Resta il "torna all'inizio". -->
        <div class="pagination-right">
          <div class="pagination-info">
            {{ positionLabel }} di {{ totalLabel }}
          </div>
          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToFirstPage"
              :disabled="isLoading || !pagination.hasPrev"
              title="Torna all'inizio"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToAdjacentPage('prev')"
              :disabled="isLoading || !pagination.hasPrev"
              title="Precedenti"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToAdjacentPage('next')"
              :disabled="isLoading || !pagination.hasNext"
              title="Successivi"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToLastPage"
              :disabled="isLoading || !pagination.hasNext"
              title="Vai alla fine"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal modifica magazzino -->
    <div
      v-if="editItem"
      class="warehouse-modal-backdrop"
      @click.self="closeEdit"
    >
      <div class="warehouse-modal">
        <button
          class="icons-button warehouse-modal__close"
          @click="closeEdit"
          type="button"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <!-- [sc-48280] "giacenze", non "magazzino": qui si scrive una
             quantità, non si configura la pagina. Stesso nome sul title della
             matita e nel toast, che dicevano tre cose diverse. -->
        <h3 class="warehouse-modal__title">Aggiorna giacenze</h3>
        <!-- [sc-48247] Il solo nome non basta a identificare l'articolo:
             varianti diverse dello stesso codice lo condividono, e qui si
             scrive una giacenza. -->
        <p class="warehouse-modal__subtitle">
          {{ editItem.itemName
          }}<span v-if="variantLabel(editItem)">
            — {{ variantLabel(editItem) }}</span
          >
        </p>

        <div class="warehouse-modal__fields">
          <template v-if="editGroup === 'area'">
            <!-- Articoli senza sqmPerBox (es. parquet senza dati imballo): input diretto m² -->
            <div v-if="editUsesDirectSqm" class="field">
              <label>M² in magazzino</label>
              <input
                type="number"
                min="0"
                step="0.01"
                v-model.number="editFields.sqmTotal"
              />
              <small>Inserisci i m² totali disponibili in magazzino</small>
            </div>
            <!-- Articoli con sqmPerBox (es. piastrelle): calcolo automatico da scatole -->
            <div v-else class="field">
              <label>Numero scatole</label>
              <input
                type="number"
                min="0"
                v-model.number="editFields.boxQuantity"
              />
              <small>Pezzi e m² calcolati automaticamente</small>
            </div>
          </template>
          <template v-else-if="editGroup === 'linear'">
            <div class="field">
              <label>Numero confezioni</label>
              <input
                type="number"
                min="0"
                v-model.number="editFields.boxQuantity"
              />
              <small>Metri lineari calcolati automaticamente</small>
            </div>
          </template>
          <template v-else>
            <div class="field">
              <label>Pezzi in magazzino</label>
              <input
                type="number"
                min="0"
                v-model.number="editFields.inWarehouse"
              />
            </div>
          </template>
        </div>

        <div class="warehouse-modal__actions">
          <button class="btn btn-secondary" @click="closeEdit" type="button">
            Annulla
          </button>
          <button
            class="btn btn-primary"
            @click="saveEdit"
            type="button"
            :disabled="editSaving"
          >
            {{ editSaving ? "Salvataggio…" : "Salva" }}
          </button>
        </div>
      </div>
    </div>

    <ItemHistoryModal
      v-if="historyItem"
      :item-id="historyItem.id"
      :item-name="historyItem.name"
      :category="historyItem.category"
      :show="!!historyItem"
      @update:show="closeHistory"
    />
  </main>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import debounce from "lodash/debounce";
import ItemHistoryModal from "../components/ItemHistoryModal.vue";
import { formatDecimal } from "../utilities/formatting";
import { variantLabel } from "../utilities/itemVariant";

const COLUMN_GROUPS = {
  area: [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ],
  linear: ["profili", "sistemi di scarico"],
  piece: ["sanitari", "rubinetteria", "box doccia", "accessori bagno"],
  furniture: ["mobili", "porte e infissi"],
};

function getCategoryGroup(category, salesUnit) {
  if (category === "piastrelle" && salesUnit === "ML") return "linear";
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(category)) return group;
  }
  return "generic";
}

const CATEGORIES = [
  "piastrelle",
  "parquet",
  "bagno",
  "rivestimenti parete",
  "laminati",
  "profili",
  "altro",
  // "decking esterno",
  // "box doccia",
  // "sanitari",
  // "rubinetteria",
  // "accessori bagno",
  // "sistemi di scarico",
  // "mobili",
  // "porte e infissi",
];

export default {
  name: "MagazzinoPage",
  components: { ItemHistoryModal },
  data() {
    return {
      CATEGORIES,
      isLoading: false,
      refreshing: false,
      refreshed: false,
      items: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 8,
        startItem: 1,
        endItem: 0,
      },
      filterSearch: "",
      filterCategory: "",
      filterInStock: false,
      // [sc-48339] Contatore delle richieste: vedi `loadItems`.
      itemsRequestId: 0,
      // [sc-48337] Stato della paginazione a cursore. `pageIndex` serve solo a
      // scrivere "17-24 di"; `currentCursor` è quello che ha prodotto la pagina
      // mostrata, e serve a ricaricarla senza spostarsi.
      pageIndex: 1,
      currentCursor: null,
      currentDirection: "next",
      // [sc-48337] Da dove si sta contando: "start" dalla prima pagina, "end"
      // dall'ultima. `pageIndex` è il numero di pagina **a partire da
      // quell'estremo**, quindi vale 1 sia sulla prima sia sull'ultima.
      //
      // Serve perché l'ultima pagina a cursore è piena e non allineata alla
      // griglia: ancorare il conto alla fine è l'unico modo di numerarla anche
      // quando il totale è fermo a 10.000 e la posizione assoluta non si sa.
      anchor: "start",
      historyItem: null,
      editItem: null,
      editFields: { boxQuantity: 0, inWarehouse: 0, sqmTotal: 0 },
      editSaving: false,
      criticalItemIds: {},
      // [sc-48280] Vista compressa: solo Codice, Nome, Giacenza e Azioni.
      // Parte attiva su schermo piccolo, dove la tabella a dieci colonne non
      // ci sta; su desktop resta estesa come prima.
      compact: false,
      isSmallScreen: false,
      compactTouched: false,
      mqSmall: null,
      // [sc-48280] Colonna Azioni collassata a striscia. Parte sempre estesa,
      // anche su schermo piccolo: si richiude dal chevron nell'intestazione
      // quando serve spazio per la Giacenza.
      actionsCollapsed: false,
      // [sc-48280] Stato della barra di scorrimento disegnata da noi.
      scrollable: false,
      // [sc-48280] Vero quando sotto la colonna Azioni sta passando altro
      // contenuto: solo allora l'ombra sul suo bordo ha un senso.
      actionsOverlapping: false,
      thumbWidth: 100,
      thumbLeft: 0,
      draggingScrollbar: false,
      tblResizeObserver: null,
    };
  },
  computed: {
    filteredItems() {
      return this.items;
    },
    activeGroup() {
      return this.filterCategory ? getCategoryGroup(this.filterCategory) : null;
    },
    // [sc-48280] Le colonne secondarie: quelle che spariscono in compresso.
    // Giacenza e Azioni non sono qui perché sono il motivo per cui si apre
    // questa pagina.
    showVariantCol() {
      return !this.compact;
    },
    showBrandCol() {
      return !this.compact;
    },
    showCategoryCol() {
      return !this.compact && !this.filterCategory;
    },
    showBoxCol() {
      if (this.compact) return false;
      return (
        !this.activeGroup ||
        this.activeGroup === "area" ||
        this.activeGroup === "linear"
      );
    },
    showSqmCol() {
      if (this.compact) return false;
      return !this.activeGroup || this.activeGroup === "area";
    },
    showMlCol() {
      if (this.compact) return false;
      return (
        !this.activeGroup ||
        this.activeGroup === "linear" ||
        this.filterCategory === "piastrelle"
      );
    },
    boxColLabel() {
      return this.activeGroup === "linear"
        ? "N. Confezioni in stock"
        : "N. Scatole in stock";
    },
    // [sc-48280] Contato dalle stesse computed che governano le colonne, non
    // più a mano: con la vista compressa le combinazioni sono troppe perché un
    // numero scritto a mano resti allineato.
    colCount() {
      let n = 3; // Codice, Nome, Giacenza
      n += 1; // Azioni
      if (this.showVariantCol) n++;
      if (this.showBrandCol) n++;
      if (this.showCategoryCol) n++;
      if (this.showBoxCol) n++;
      if (this.showSqmCol) n++;
      if (this.showMlCol) n++;
      return n;
    },
    editGroup() {
      return getCategoryGroup(
        this.editItem?.category,
        this.editItem?.salesUnit
      );
    },
    // True quando l'articolo è di tipo area ma non ha sqmPerBox configurato
    // (es. parquet senza dati imballo): in questo caso si inseriscono i m² direttamente.
    editUsesDirectSqm() {
      return (
        this.editGroup === "area" && !(Number(this.editItem?.sqmPerBox) > 0)
      );
    },
    // [sc-48337] Il totale è fermato a 10.000 dall'API: oltre quella soglia si
    // mostra "10.000+" invece di un numero preciso che nessuno userebbe e che
    // costava una scansione dell'intera tabella a ogni battuta.
    // [sc-48337] La posizione la conta il client: a cursore l'API non sa a che
    // pagina siamo. Saltando alla fine con un conteggio fermo la posizione non
    // è conoscibile — `pageIndex` è null — e si dice quello che si sa: quante
    // righe si stanno guardando.
    positionLabel() {
      const n = this.items.length;
      if (!n) return "0-0";
      const perPage = this.pagination.itemsPerPage || 8;
      // Quante righe stanno fra questa pagina e l'estremo da cui si conta.
      const scarto = (this.pageIndex - 1) * perPage;
      // Stesso separatore delle migliaia del totale: "335.612-335.619 di
      // 335.635" si legge, "335612-335619 di 335.635" no.
      const fmt = (v) => v.toLocaleString("it-IT");

      if (this.anchor === "start") {
        return `${fmt(scarto + 1)}-${fmt(scarto + n)}`;
      }

      // Contando dalla fine: con un totale vero si risale alla posizione
      // assoluta, ed è esatta anche se l'ultima pagina non cade sui multipli
      // di otto.
      if (!this.pagination.totalIsCapped) {
        const ultimo = (this.pagination.totalItems ?? n) - scarto;
        return `${fmt(ultimo - n + 1)}-${fmt(ultimo)}`;
      }
      // Totale fermo a 10.000: la posizione assoluta non è conoscibile, ma la
      // distanza dalla fine sì. Meglio "ultimi 9-16" di un generico conteggio
      // che si azzera a ogni pagina.
      return `ultimi ${fmt(scarto + 1)}-${fmt(scarto + n)}`;
    },
    totalLabel() {
      const n = this.pagination.totalItems ?? 0;
      return this.pagination.totalIsCapped
        ? `${n.toLocaleString("it-IT")}+`
        : n.toLocaleString("it-IT");
    },
  },
  methods: {
    formatDecimal,
    variantLabel,

    // [sc-48280] La vista compressa segue il breakpoint, ma solo finché
    // l'utente non decide da sé: una volta toccato il toggle, `compactTouched`
    // impedisce che una rotazione dello schermo gli rimetta le colonne come
    // pare a noi.
    applyScreenSize(mq) {
      this.isSmallScreen = mq.matches;
      // Su desktop la compressione si spegne comunque: lì il toggle non è
      // visibile e l'utente resterebbe con le colonne nascoste e nessun modo
      // di riaprirle. Stesso ragionamento per la colonna Azioni, che su
      // desktop ci sta senza togliere spazio a nulla.
      if (!mq.matches) {
        this.compact = false;
        this.actionsCollapsed = false;
        return;
      }
      // [sc-48280] La colonna Azioni parte **estesa** anche su schermo
      // piccolo: le azioni si vedono senza doverle cercare, e chi ha bisogno
      // di spazio per la Giacenza la richiude dal chevron. Scelta di Andrea:
      // meglio mostrare quello che si può fare e lasciare che sia l'utente a
      // nasconderlo, che il contrario.
      if (!this.compactTouched) this.compact = true;
    },
    toggleCompact() {
      this.compact = !this.compact;
      this.compactTouched = true;
      // [sc-48280] Mostrando le altre colonne, quelle nuove nascono fuori
      // schermo a destra: senza questo sembra che il bottone non abbia fatto
      // nulla. `$nextTick` perché le colonne devono esistere prima che
      // `scrollWidth` valga qualcosa.
      if (!this.compact) this.scrollToLastColumn();
    },
    scrollToLastColumn() {
      this.$nextTick(() => {
        const wrap = this.$refs.tblWrap;
        if (!wrap) return;
        wrap.scrollTo({ left: wrap.scrollWidth, behavior: "smooth" });
      });
    },

    // ── [sc-48280] Barra di scorrimento orizzontale ──────────────────────
    // Ricalcolata su scroll, sul resize della tabella (le colonne cambiano
    // larghezza al variare dei dati) e dopo ogni caricamento.
    syncScrollbar() {
      const wrap = this.$refs.tblWrap;
      if (!wrap) return;
      const { clientWidth, scrollWidth, scrollLeft } = wrap;
      // Un pixel di tolleranza: gli arrotondamenti sub-pixel del layout
      // farebbero comparire la barra su tabelle che in realtà ci stanno.
      this.scrollable = scrollWidth - clientWidth > 1;
      // A fine corsa la colonna sticky è al suo posto naturale e non copre
      // niente: l'ombra sparisce con lei.
      this.actionsOverlapping = scrollWidth - clientWidth - scrollLeft > 1;
      if (!this.scrollable) return;
      // Minimo 10%: su tabelle molto larghe il cursore diventerebbe un punto
      // impossibile da afferrare.
      this.thumbWidth = Math.max((clientWidth / scrollWidth) * 100, 10);
      const corsa = scrollWidth - clientWidth;
      const p = corsa > 0 ? scrollLeft / corsa : 0;
      this.thumbLeft = p * (100 - this.thumbWidth);
    },
    scrollToPointer(e) {
      const bar = this.$refs.scrollbar;
      const wrap = this.$refs.tblWrap;
      if (!bar || !wrap) return;
      const rect = bar.getBoundingClientRect();
      const thumbPx = (rect.width * this.thumbWidth) / 100;
      // Il cursore si centra sul dito: trascinandolo dal bordo salterebbe.
      const corsaBarra = rect.width - thumbPx;
      const x = e.clientX - rect.left - thumbPx / 2;
      const p = corsaBarra > 0 ? Math.min(Math.max(x / corsaBarra, 0), 1) : 0;
      wrap.scrollLeft = p * (wrap.scrollWidth - wrap.clientWidth);
    },
    onScrollbarPointerDown(e) {
      this.draggingScrollbar = true;
      this.$refs.scrollbar?.setPointerCapture?.(e.pointerId);
      this.scrollToPointer(e);
    },
    onScrollbarPointerMove(e) {
      if (this.draggingScrollbar) this.scrollToPointer(e);
    },
    onScrollbarPointerUp(e) {
      this.draggingScrollbar = false;
      try {
        this.$refs.scrollbar?.releasePointerCapture?.(e.pointerId);
      } catch {
        /* il puntatore può essere già stato rilasciato dal browser */
      }
    },

    async handleRefresh() {
      this.refreshing = true;
      this.refreshed = false;
      try {
        await this.reloadCurrent();
      } finally {
        this.refreshing = false;
        this.refreshed = true;
        setTimeout(() => {
          this.refreshed = false;
        }, 1500);
      }
    },

    async toggleCritical(id) {
      try {
        const { data } = await axiosInstance.patch(`/item/${id}/critical`);
        this.criticalItemIds = {
          ...this.criticalItemIds,
          [id]: data.isCritical,
        };
      } catch {
        this.$toast.error("Errore durante l'aggiornamento della criticità.");
      }
    },
    itemGroup(item) {
      return getCategoryGroup(item.category, item.salesUnit);
    },
    openHistory(item) {
      // [sc-48247] La variante entra nel nome mostrato: lo storico è per
      // articolo, e articoli diversi condividono lo stesso itemName.
      const variante = variantLabel(item);
      this.historyItem = {
        id: item.id,
        name: variante ? `${item.itemName} — ${variante}` : item.itemName,
        category: item.category,
      };
    },
    closeHistory() {
      this.historyItem = null;
    },

    openEdit(item) {
      this.editItem = item;
      const wh = item.warehouse ?? {};
      this.editFields = {
        boxQuantity: wh.boxQuantity ?? 0,
        inWarehouse: wh.inWarehouse ?? 0,
        sqmTotal: wh.sqmTotal ? Number(wh.sqmTotal) : 0,
      };
    },
    closeEdit() {
      this.editItem = null;
      this.editSaving = false;
    },
    async saveEdit() {
      this.editSaving = true;
      try {
        const group = this.editGroup;
        const payload = {};
        if (group === "area") {
          if (this.editUsesDirectSqm) {
            payload.sqmTotal = this.editFields.sqmTotal;
          } else {
            payload.boxQuantity = this.editFields.boxQuantity;
          }
        } else if (group === "linear") {
          payload.boxQuantity = this.editFields.boxQuantity;
        } else {
          payload.inWarehouse = this.editFields.inWarehouse;
        }
        await axiosInstance.patch(`/item/update/${this.editItem.id}`, payload);
        this.$toast.success("Giacenze aggiornate.");
        this.closeEdit();
        this.reloadCurrent();
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          "Errore durante l'aggiornamento del magazzino.";
        this.$toast.warning(msg);
      } finally {
        this.editSaving = false;
      }
    },

    // [sc-48339] Invio: esegue subito la ricerca in attesa invece di
    // aggiungerne una. `flush()` non fa nulla se non c'è niente in coda.
    onSearchNow() {
      this.debouncedSearch.flush();
    },
    // [sc-48339] Il `flush()` va dopo il `nextTick`: svuotando il campo si fa
    // scattare il watcher, e annullare il debounce *prima* lo lascerebbe
    // ripartire subito dopo. Così la richiesta è una sola e parte senza attesa.
    onResetSearch() {
      this.filterSearch = "";
      this.$nextTick(() => this.debouncedSearch.flush());
    },
    // [sc-48339] `reqId` progressivo, come nello store per Listini: qui la
    // chiamata è fatta dal componente, quindi la guardia sta qui. Senza, fra
    // due ricerche accavallate vince quella che risponde per ultima — che è
    // sistematicamente la più generica, perché è anche la più lenta.
    // [sc-48337] Spostamento di una pagina, avanti o indietro.
    //
    // Il cursore da mandare è l'estremo della pagina attuale — l'ultimo id
    // andando avanti, il primo andando indietro — e l'API restituisce le otto
    // righe oltre quel confine. Con `OFFSET` il database doveva invece produrre
    // e buttare via tutte le righe precedenti: 172 ms a pagina 500 contro 1,4.
    goToAdjacentPage(direction) {
      if (this.isLoading) return;
      const avanti = direction === "next";
      if (avanti ? !this.pagination.hasNext : !this.pagination.hasPrev) return;
      const cursor = avanti
        ? this.pagination.nextCursor
        : this.pagination.prevCursor;
      if (cursor == null) return;
      // Ancorati alla fine, "successivi" avvicina all'ultima pagina e quindi
      // fa **calare** la distanza dall'estremo.
      const verso = this.anchor === "start" ? 1 : -1;
      this.pageIndex = Math.max(1, this.pageIndex + (avanti ? verso : -verso));
      this.loadItems({ cursor, direction });
    },
    goToFirstPage() {
      if (this.isLoading) return;
      this.loadItems();
    },
    // [sc-48337] Salto alla fine. Non serve sapere quante pagine ci sono — è
    // proprio il conteggio che si è tolto: l'API legge dalla coda invertendo
    // l'ordinamento, che costa come leggere dalla testa.
    //
    // Da qui in poi il conteggio è ancorato alla fine: vedi `positionLabel`.
    goToLastPage() {
      if (this.isLoading || !this.pagination.hasNext) return;
      this.loadItems({ direction: "prev" });
    },
    // Ricarica la pagina mostrata senza spostarsi: serve dopo un aggiornamento
    // di giacenza, che non deve far saltare l'utente altrove.
    reloadCurrent() {
      return this.loadItems({
        cursor: this.currentCursor,
        direction: this.currentDirection,
      });
    },
    // [sc-48339] `reqId` progressivo, come nello store per Listini: qui la
    // chiamata è fatta dal componente, quindi la guardia sta qui. Senza, fra
    // due ricerche accavallate vince quella che risponde per ultima — che è
    // sistematicamente la più generica, perché è anche la più lenta.
    async loadItems({ cursor = null, direction = "next" } = {}) {
      const reqId = ++this.itemsRequestId;
      // Senza cursore si riparte dall'inizio: ricerca nuova, cambio filtro o
      // primo caricamento.
      if (cursor == null) {
        // Senza cursore si riparte da un estremo: la prima pagina andando
        // avanti, l'ultima andando indietro. In entrambi i casi la distanza da
        // quell'estremo è 1.
        this.anchor = direction === "prev" ? "end" : "start";
        this.pageIndex = 1;
      }
      this.currentCursor = cursor;
      this.currentDirection = direction;
      this.isLoading = true;
      try {
        const { data } = await axiosInstance.get("/item/get-all", {
          params: {
            limit: 8,
            ...(this.filterCategory ? { category: this.filterCategory } : {}),
            ...(this.filterSearch ? { search: this.filterSearch } : {}),
            ...(this.filterInStock ? { inStock: true } : {}),
            // [sc-48337] `direction` va mandato anche **senza** cursore: è il
            // salto all'ultima pagina, che si esprime proprio come "indietro
            // dalla fine". Mandarlo solo insieme al cursore faceva restituire
            // all'API la prima pagina, e da lì `hasPrev` tornava false: i tasti
            // indietro e inizio restavano disabilitati.
            ...(cursor != null ? { cursor } : {}),
            ...(cursor != null || direction === "prev" ? { direction } : {}),
          },
        });
        if (reqId !== this.itemsRequestId) return; // risposta obsoleta
        this.items = Array.isArray(data.results) ? data.results : [];
        const newCritical = {};
        this.items.forEach((item) => {
          if (item.warehouse?.isCritical) newCritical[item.id] = true;
        });
        this.criticalItemIds = { ...this.criticalItemIds, ...newCritical };
        const p = data.pagination;
        // [sc-48337] La posizione la conta il client: a cursore l'API non sa a
        // che pagina siamo, e non deve saperlo. `endItem` si calcola dalle
        // righe davvero ricevute, non dal prodotto pagina x limite, che
        // sull'ultima pagina darebbe un numero più alto del totale.
        this.pagination = {
          currentPage: this.pageIndex,
          totalPages: p.totalPages,
          totalItems: p.totalItems,
          itemsPerPage: p.itemsPerPage,
          totalIsCapped: Boolean(p.totalIsCapped),
          hasNext: Boolean(p.hasNext),
          hasPrev: Boolean(p.hasPrev),
          nextCursor: p.nextCursor ?? null,
          prevCursor: p.prevCursor ?? null,
        };
      } catch (err) {
        // Anche il 404 svuota la lista: una ricerca superata che non trova
        // nulla non deve cancellare i risultati di quella corrente.
        if (reqId !== this.itemsRequestId) return;
        if (err.response?.status === 404) {
          this.items = [];
          return;
        }
        this.$toast.error("Errore durante il caricamento del magazzino.");
      } finally {
        // `isLoading` lo spegne solo la richiesta ancora valida, altrimenti una
        // risposta vecchia toglierebbe lo spinner mentre l'ultima è in volo.
        if (reqId === this.itemsRequestId) this.isLoading = false;
        // [sc-48280] Dati nuovi = colonne di larghezza diversa. Il
        // ResizeObserver lo coglie, ma solo dopo il paint: questo evita che la
        // barra resti un istante con la misura della pagina precedente.
        this.$nextTick(this.syncScrollbar);
      }
    },
  },
  // [sc-48339] Il debounce si crea per istanza, non a livello di modulo.
  created() {
    this.debouncedSearch = debounce(() => this.loadItems(), 300);
  },
  mounted() {
    this.loadItems();
    // [sc-48280] `matchMedia` e non un controllo di `innerWidth` al mount: la
    // rotazione di un tablet cambia il breakpoint senza ricaricare la pagina,
    // e lascerebbe il toggle nascosto con la tabella troppo larga.
    this.mqSmall = window.matchMedia("(max-width: 1024px)");
    this.applyScreenSize(this.mqSmall);
    this.mqSmall.addEventListener("change", this.applyScreenSize);

    // [sc-48280] La barra va risincronizzata su scroll e ogni volta che la
    // tabella cambia larghezza: colonne mostrate o nascoste, dati nuovi con
    // nomi più lunghi, rotazione dello schermo. Il ResizeObserver le copre
    // tutte senza doverle elencare.
    this.$nextTick(() => {
      this.$refs.tblWrap?.addEventListener("scroll", this.syncScrollbar, {
        passive: true,
      });
      if (window.ResizeObserver && this.$refs.tblWrap) {
        this.tblResizeObserver = new ResizeObserver(this.syncScrollbar);
        this.tblResizeObserver.observe(this.$refs.tblWrap);
        const table = this.$refs.tblWrap.querySelector("table");
        if (table) this.tblResizeObserver.observe(table);
      }
      this.syncScrollbar();
    });
  },
  beforeUnmount() {
    this.mqSmall?.removeEventListener("change", this.applyScreenSize);
    this.$refs.tblWrap?.removeEventListener("scroll", this.syncScrollbar);
    this.tblResizeObserver?.disconnect();
    // Senza questo, uscendo dalla pagina subito dopo aver digitato resta una
    // chiamata in coda su un componente ormai smontato.
    this.debouncedSearch?.cancel();
  },
  watch: {
    // Categoria e "solo disponibili" restano immediati: sono click singoli, non
    // c'è una raffica da assorbire.
    filterCategory() {
      this.loadItems();
    },
    // [sc-48339] Era `loadItems(1)` diretto: una chiamata per ogni tasto
    // premuto, e vinceva quella che rispondeva per ultima invece dell'ultima
    // digitata. Il debounce toglie le chiamate intermedie; il `reqId` in
    // `loadItems` copre le due che si accavallano comunque.
    filterSearch() {
      this.debouncedSearch();
    },
    filterInStock() {
      this.loadItems();
    },
    // [sc-48280] La colonna cambia larghezza con una transizione: il
    // ResizeObserver la segue durante l'animazione, questo assicura la misura
    // finale anche se l'ultimo frame non genera un evento.
    actionsCollapsed() {
      this.$nextTick(this.syncScrollbar);
      setTimeout(this.syncScrollbar, 260);
    },
  },
};
</script>

<style src="@/css/StockPage.css" />
<style scoped>
.warehouse-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  /* [sc-48280] Il backdrop non aveva padding: su telefono il modale toccava i
     bordi dello schermo. */
  padding: 16px;
  z-index: 1000;
}
.warehouse-modal {
  background: #fff;
  border-radius: 8px;
  padding: 28px 32px;
  /* [sc-48280] `min-width: 320px` impediva al modale di rimpicciolirsi sotto
     quella soglia: sommato al padding del backdrop, su un telefono da 360px
     usciva dallo schermo. `min()` tiene i 420px dove c'è spazio e cede dove
     non ce n'è. */
  width: min(420px, 100%);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}
@media (max-width: 505px) {
  /* Padding interno ridotto: con la tastiera aperta su un campo numerico lo
     spazio verticale utile si dimezza. */
  .warehouse-modal {
    padding: 20px 18px;
  }
  .warehouse-modal__fields {
    gap: 12px;
    margin-bottom: 18px;
  }
}
.warehouse-modal__close {
  position: absolute;
  top: 12px;
  right: 12px;
}
.warehouse-modal__title {
  margin: 0 0 4px;
  font-size: 1.1rem;
}
.warehouse-modal__subtitle {
  margin: 0 0 20px;
  color: #666;
  font-size: 0.9rem;
}
.warehouse-modal__fields {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 24px;
}
.warehouse-modal__fields .field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.warehouse-modal__fields label {
  font-size: 0.85rem;
  font-weight: 600;
}
.warehouse-modal__fields input {
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
}
.warehouse-modal__fields small {
  font-size: 0.78rem;
  color: #888;
}
/* [sc-48280] Colonna Azioni agganciata al bordo destro: la tabella scorre
   sotto, le tre icone restano dove sono. Senza questo, su mobile bisogna
   attraversare tutte le colonne per premerne una.

   `background: inherit` e non un colore fisso: la cella deve seguire lo zebra
   striping, l'hover e il giallo delle righe critiche, altrimenti si vedrebbe
   il contenuto scorrerle sotto. Nel thead l'inherit non basta — il colore sta
   su `thead`, non sulla `tr` — quindi è esplicito. */
.tbl tbody .actions-cell,
.tbl thead .actions-head {
  position: sticky;
  right: 0;
  z-index: 2;
  /* [sc-48280] Larghezza animata: `max-width` fa da cappio alla colonna e la
     transizione fra i due valori è ciò che si vede muoversi. Il valore alto è
     solo un limite superiore comodo — la colonna resta larga quanto il suo
     contenuto. */
  max-width: 240px;
  overflow: hidden;
  transition: max-width 0.22s ease, padding 0.22s ease, box-shadow 0.22s ease;
}

/* [sc-48280] L'ombra segna il bordo di uno scorrimento in corso, quindi ha
   senso solo quando sotto la colonna sta passando qualcosa. A tabella ferma a
   fine corsa, o quando non c'è nulla da scorrere, restava una riga verticale
   sospesa nel vuoto. */
.tbl tbody .actions-cell--overlapping,
.tbl thead .actions-head--overlapping {
  box-shadow: -6px 0 6px -6px rgba(0, 0, 0, 0.25);
}
.tbl tbody .actions-cell {
  background: inherit;
}
.tbl thead .actions-head {
  background: #fff;
  text-align: left;
  white-space: nowrap;
}

/* [sc-48280] Stato collassato: la colonna si riduce a una striscia. Estesa
   occupava ~175px su 535 e nascondeva la Giacenza (screenshot della review).

   ⚠️ Solo il padding ORIZZONTALE va azzerato. Azzerandolo tutto — primo
   tentativo — la riga perdeva anche i 10px sopra e sotto e la tabella si
   accorciava di parecchio ogni volta che si comprimeva la colonna. */
.tbl thead .actions-head--collapsed,
.tbl tbody .actions-cell--collapsed {
  max-width: 34px;
  padding-left: 0;
  padding-right: 0;
  text-align: center;
}

/* I bottoni restano al loro posto e occupano la stessa altezza di sempre:
   sono soltanto invisibili e non cliccabili. È il modo più diretto per avere
   righe alte uguali nei due stati, senza replicare a mano la somma
   icona + padding. Sfumano e scivolano invece di sparire di colpo. */
.tbl tbody .actions-cell .icons-button {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.tbl tbody .actions-cell--collapsed .icons-button {
  visibility: hidden;
  opacity: 0;
  transform: translateX(10px);
  flex: 0 0 auto;
  pointer-events: none;
}

/* Il chevron ruota invece di essere sostituito da un'altra icona: è lo stesso
   oggetto che cambia verso, e si capisce che il comando è uno solo. */
.actions-toggle i {
  transition: transform 0.22s ease;
}

@media (prefers-reduced-motion: reduce) {
  .tbl tbody .actions-cell,
  .tbl thead .actions-head,
  .tbl tbody .actions-cell .icons-button,
  .actions-toggle i {
    transition: none;
  }
}
.actions-toggle {
  border: none;
  background: none;
  padding: 0 6px 0 0;
  color: #0077a8;
  cursor: pointer;
  font-size: 14px;
}
.actions-head--collapsed .actions-toggle {
  padding: 0;
}

/* [sc-48280] La riga apre l'aggiornamento giacenze: il puntatore lo dice. */
.item-row--clickable {
  cursor: pointer;
}

/* [sc-48280] La scrollbar nativa è nascosta e sostituita da `.table-scrollbar`,
   disegnata sotto la tabella: quella di sistema si nasconde da sola quando non
   si sta scorrendo e non esiste un modo affidabile per tenerla a schermo su
   mobile. Lo scroll nativo resta attivo — si continua a trascinare la tabella
   con il dito, la barra è in più, non al posto. */
.tbl-wrap {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.tbl-wrap::-webkit-scrollbar {
  display: none;
}

.table-scrollbar {
  position: relative;
  height: 10px;
  margin: 6px 0 2px;
  background: #e6eef2;
  border-radius: 5px;
  cursor: pointer;
  /* Il browser non deve interpretare il trascinamento come scroll della
     pagina: lo gestiamo noi con i pointer event. */
  touch-action: none;
}
.table-scrollbar__thumb {
  position: absolute;
  top: 0;
  height: 100%;
  background: #0077a8;
  border-radius: 5px;
  transition: left 0.05s linear;
}

.col-giacenza {
  background-color: #d6f3ff;
  font-weight: 600;
}
.item-row--critical {
  background-color: #fff8e1 !important;
}
.item-row--critical .col-giacenza {
  background-color: #fff3cd !important;
}
.item-row--critical:hover {
  background-color: #fdefc3 !important;
}
.btn-critical--active {
  color: #e65100;
}
.damaged-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #fef2f2;
  border: 1px solid #fca5a5;
  color: #dc2626;
  font: 600 11px "Inter", sans-serif;
  white-space: nowrap;
  cursor: default;
}
.item-row:hover .col-giacenza {
  background-color: #e0e0e0;
}
.warehouse-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
